import { FC, ReactNode } from "react";
import {
  Box,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowForwardIos as ArrowForwardIosIcon } from "@material-ui/icons";

import { Stat } from "components";
import { StatProps } from "components/Stat";
import theme, { Theme } from "theme";

const useStyles = makeStyles<Theme, { hasAction: boolean }>(() => ({
  cardActionArea: {
    height: "100%",
    minHeight: "82px",
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    "& .nextArrow": {
      fontSize: "0.75rem",
      transition: "transform 200ms",
    },
  },
  cardActionAreaHoverStyles: {
    color: theme.custom.palette.purple.main, // this determines the backgroundColor on hover
    "&:hover": {
      cursor: "pointer",
      borderColor: theme.custom.palette.purple.main,
      backgroundColor: theme.custom.palette.purple.lightest,
      "& .nextArrow": {
        transform: "translateX(4px)",
      },
      "& .MuiCardActions-root": {
        opacity: 1,
      },
    },
  },
  cardContainer: {
    color: theme.palette.text.primary, // resets color, the parent action area should have color blue for hover effect
    width: "100%",
    height: "100%",
    display: "flex",
    flexWrap: "wrap",
  },
  cardContent: {
    width: "100%",
    padding: 0,
    "&:last-child": {
      padding: 0,
    },
  },
  cardHeaderContainer: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
  cardHeader: {
    padding: 0,
    paddingBottom: theme.spacing(1),
    textAlign: "left",
  },
  cardActions: {
    color: theme.custom.palette.purple.main,
    fontSize: "0.875rem",
    letterSpacing: "1.2px",
    opacity: 0.5,
    padding: 0,
    width: "100%",
  },
  offersAwaitingAcceptanceButton: {
    color: theme.custom.palette.green.main,
    backgroundColor: theme.custom.palette.green.light,
    fontWeight: theme.typography.fontWeightMedium,
    textTransform: "none",
    pointerEvents: "none",
  },
}));

export interface CardActionAreaProps {
  action?: {
    label: string;
    handleClick?: () => void;
  };
  cardHeader?: { title: string; subHeader?: ReactNode };
  stats: StatProps[];
  isLoadingStats?: boolean;
  topRightAction?: ReactNode;
  lastUpdated?: string;
}

const CardActionArea: FC<CardActionAreaProps> = ({
  action,
  cardHeader,
  stats,
  isLoadingStats,
  topRightAction,
  lastUpdated,
}) => {
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const classes = useStyles({
    hasAction: !!action,
  });

  return (
    <Box
      className={`${classes.cardActionArea} ${
        !!action ? classes.cardActionAreaHoverStyles : ""
      }`}
      onClick={action?.handleClick}
    >
      <Box className={classes.cardContainer}>
        <Box className={classes.cardHeaderContainer} flexWrap="wrap">
          {cardHeader && (
            <Box pr={2}>
              <CardHeader
                className={classes.cardHeader}
                title={cardHeader.title}
                titleTypographyProps={{
                  style: {
                    fontWeight: theme.typography.fontWeightMedium,
                    fontSize: "1.125rem",
                  },
                }}
                subheader={cardHeader.subHeader}
                subheaderTypographyProps={{
                  color: "textPrimary",
                }}
              />
            </Box>
          )}
          {topRightAction && (
            <Box
              display="flex"
              justifyContent={isSmUp ? "flex-end" : "center"}
              flexGrow={1}
              maxHeight="2.2rem"
            >
              {topRightAction}
            </Box>
          )}
        </Box>
        <CardContent className={classes.cardContent}>
          {isLoadingStats ? (
            <CircularProgress size={30} />
          ) : (
            <Grid container justify="space-between">
              {stats.map((stat, index) => (
                <Grid
                  item
                  key={index}
                  xs={stats.length > 2 && isSmUp ? undefined : 6}
                  sm={stats.length > 2 ? undefined : 6}
                >
                  <Box
                    display="flex"
                    justifyContent={
                      stats.length > 2
                        ? isSmUp
                          ? "flex-start"
                          : "center"
                        : "center"
                    }
                    width="100%"
                  >
                    <Stat
                      value={stat.value}
                      valueAdornment={stat.valueAdornment}
                      label={stat.label}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
          >
            {lastUpdated && (
              <Box width="100%" textAlign="left">
                <Typography variant="body2" color="textSecondary">
                  {`${isSmUp ? "Last Updated: " : "Updated: "}${lastUpdated}`}
                </Typography>
              </Box>
            )}
            {!isLoadingStats && action && (
              <CardActions className={classes.cardActions}>
                <Box
                  width="100%"
                  display="flex"
                  justifyContent="end"
                  alignItems="center"
                >
                  {action.label}
                  <ArrowForwardIosIcon fontSize="small" className="nextArrow" />
                </Box>
              </CardActions>
            )}
          </Box>
        </CardContent>
      </Box>
    </Box>
  );
};

export default CardActionArea;
